import React, { memo, useRef, useState, useMemo } from 'react'
import cn from 'classnames'
import styled from 'styled-components'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { BurgerMenuButton } from '../burger-menu-button'
import Text from '../../ui/Text'
import { useSignOut } from '../sign-out'
import { Drawer } from '../drawer'
import { Link, LinkType } from '../page-header'
import useOnClickOutside from '../../common/hooks/useOnClickOutside'
import useText from '../../common/hooks/useText'

const Wrapper = styled.div`
    font-weight: var(--font-weight-bold);
    display: flex;
    align-items: center;
    gap: 34px;
    font-size: 1.125rem;
`

const BurgerButton = styled.div`
    width: fit-content;
    height: 20px;
    z-index: 99999;

    &:hover {
        cursor: pointer;
    }

    @media screen and (max-width: 700px) {
        transform: scale(0.8);
    }
`

const SelectedRoute = styled.div`
    font-weight: var(--font-weight-bold);
    font-size: 1.25rem;
    letter-spacing: 0.01em;
    color: ${({ theme }) => theme.font.midDarkColor};

    @media screen and (max-width: 700px) {
        display: none;
    }
`

export type HeaderDrawerProps = {
    links: Array<LinkType | null | undefined>
    disableLogout?: boolean
    logoutDisabledReason?: string
}

const NotificationDot = styled.span`
    width: 8px;
    height: 8px;
    background: ${({ theme }) => theme.brandColors.CA_Error_Red};
    border-radius: 50%;
    display: inline-block;
    margin-left: 6px;
`

const DrawerMenuItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`

const DrawerSubMenuHeader = styled.div`
    pointer-events: none;
    cursor: default;
    color: ${({ theme }) => theme.font.midLightColor};
`

export const PageHeaderDrawer = memo(function HeaderDrawer({
    links,
    disableLogout = false,
    logoutDisabledReason,
}: HeaderDrawerProps): React.ReactElement {
    const text = useText()
    const callSignOut = useSignOut()
    const { pathname } = useLocation()
    const [openDrawer, setOpenDrawer] = useState(false)

    const safeLinks = useMemo(() => links.filter((l): l is LinkType => !!l), [links])

    const containerRef = useRef<HTMLDivElement>(null)

    useOnClickOutside(containerRef, () => {
        setOpenDrawer(false)
    })

    const selectedRoute = useMemo(() => {
        const directMatch = safeLinks.find((l) => l.href === pathname)

        if (directMatch) return directMatch.displayLabelCode || directMatch.labelCode

        if (pathname.startsWith('/billing')) {
            return 'lblBilling'
        }

        for (const link of safeLinks) {
            if (link.subMenu) {
                const matchingSubLink = link.subMenu.find(
                    (subLink) => subLink.href && pathname.includes(subLink.href.split('/')[1])
                )
                if (matchingSubLink) {
                    return matchingSubLink.displayLabelCode || matchingSubLink.labelCode
                }
            }
        }

        return null
    }, [pathname, safeLinks])

    const isMenuItemSelected = (item: LinkType): boolean => {
        if (!item.href) {
            return false
        }

        if (item.subMenu && item.subMenu.length > 0) {
            const itemBasePath = item.href.split('/')[1]
            const pathParts = pathname.split('/')

            return pathParts.length <= 2 && pathname.includes(itemBasePath)
        }

        return pathname?.includes(item.href.split('/')[1])
    }

    const isSubmenuItemSelected = (href: string): boolean => {
        const subItemPath = href.split('/').filter(Boolean)
        const currentPathParts = pathname.split('/').filter(Boolean)

        if (subItemPath.length > 1) {
            return subItemPath.every((part, index) => currentPathParts[index] === part)
        }

        return (
            pathname.includes(href.split('/')[1]) &&
            (currentPathParts.length === subItemPath.length ||
                (href.split('/')[1] === currentPathParts[0] && currentPathParts.length > 1))
        )
    }

    return (
        <Wrapper ref={containerRef}>
            {selectedRoute ? (
                <SelectedRoute
                    data-testid="location"
                    style={{ visibility: selectedRoute && !openDrawer ? 'visible' : 'hidden' }}
                >
                    <h4>
                        <Text code={selectedRoute} />
                    </h4>
                </SelectedRoute>
            ) : null}
            <BurgerButton onClick={() => setOpenDrawer(!openDrawer)} data-testid="burgerButton">
                <BurgerMenuButton open={openDrawer} />
            </BurgerButton>

            <Drawer className={cn({ open: openDrawer }, 'gap-3')} data-testid="drawer-menu">
                {safeLinks.map((l, i) => (
                    <div key={i}>
                        <DrawerMenuItem>
                            {l.href ? (
                                <Link
                                    as={RouterLink}
                                    to={l.href}
                                    selected={isMenuItemSelected(l)}
                                    data-testid={l.href}
                                    onClick={() => setOpenDrawer(false)}
                                    onMouseEnter={l.prefetch}
                                    light
                                >
                                    <Text code={l.labelCode} />
                                    {l.hasNotification && <NotificationDot />}
                                </Link>
                            ) : (
                                <DrawerSubMenuHeader data-testid={`menu-${l.labelCode}`}>
                                    <Text code={l.labelCode} />
                                    {l.hasNotification && <NotificationDot />}
                                </DrawerSubMenuHeader>
                            )}
                        </DrawerMenuItem>

                        {l.subMenu && (
                            <div
                                className="flex flex-column gap-3 mt-3 ml-4 font-bold text-base"
                                data-testid={`submenu-${l.href || l.labelCode}`}
                            >
                                {l.subMenu.map((subLink, subIndex) =>
                                    subLink.href ? (
                                        <Link
                                            key={subIndex}
                                            as={RouterLink}
                                            to={subLink.href}
                                            light
                                            onClick={() => setOpenDrawer(false)}
                                            selected={isSubmenuItemSelected(subLink.href)}
                                            data-testid={subLink.href}
                                        >
                                            <Text code={subLink.labelCode} />
                                            {subLink.hasNotification && <NotificationDot />}
                                        </Link>
                                    ) : null
                                )}
                            </div>
                        )}
                    </div>
                ))}

                <Link
                    onClick={disableLogout ? undefined : callSignOut}
                    data-testid="logOut"
                    selected={false}
                    light
                    className={cn({ 'pointer-events-none opacity-50': disableLogout })}
                    aria-disabled={disableLogout}
                    tabIndex={disableLogout ? -1 : undefined}
                >
                    {text('btnLogout')}
                </Link>
                {disableLogout && logoutDisabledReason ? (
                    <p data-testid="drawer-logout-disabled-reason" className="mt-2 mb-0 text-sm line-height-3 text-600">
                        {logoutDisabledReason}
                    </p>
                ) : null}
            </Drawer>
        </Wrapper>
    )
})
